<template>
  <Teleport to="body">
    <Transition name="modal" appear>
      <div class="fixed inset-0 z-50 flex items-center justify-center p-4" @click.self="$emit('close')">
        <div class="absolute inset-0 bg-black/50 backdrop-blur-sm"></div>

        <div class="relative w-full max-w-3xl bg-white dark:bg-[#1C2128] rounded-2xl shadow-2xl overflow-hidden animate-in flex flex-col md:flex-row max-h-[90vh]">

          <!-- Left: informational panel, animated gradient -->
          <div class="gradient-panel relative overflow-hidden flex-shrink-0 md:w-[38%] p-7 sm:p-8 flex flex-col justify-between text-white">
            <div class="blob blob-1"></div>
            <div class="blob blob-2"></div>

            <div class="relative z-10">
              <div class="w-12 h-12 rounded-xl bg-white/15 backdrop-blur-sm flex items-center justify-center mb-5 ring-1 ring-white/20">
                <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" :d="infoIcon.path"/>
                </svg>
              </div>
              <h3 class="text-lg font-bold leading-snug mb-2.5">{{ infoHeading }}</h3>
              <p class="text-sm text-blue-50/90 leading-relaxed">{{ infoBody }}</p>
            </div>

            <ul class="relative z-10 space-y-3 mt-8">
              <li v-for="point in infoPoints" :key="point" class="flex items-start gap-2.5">
                <svg class="w-4 h-4 text-blue-100 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M4.5 12.75l6 6 9-13.5"/>
                </svg>
                <span class="text-xs text-blue-50/90 leading-relaxed">{{ point }}</span>
              </li>
            </ul>
          </div>

          <!-- Right: form -->
          <div class="flex-1 flex flex-col min-w-0 overflow-y-auto">
            <div class="flex items-center justify-between px-6 sm:px-7 pt-6 pb-4">
              <h3 class="text-base font-semibold text-gray-900 dark:text-white">{{ title }}</h3>
              <button @click="$emit('close')" type="button"
                class="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </button>
            </div>

            <div class="px-6 sm:px-7 space-y-4 flex-1">
              <div>
                <label class="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">Name</label>
                <input ref="nameInput" v-model="name" type="text" :placeholder="type === 'company' ? 'e.g. Acme Corp' : 'e.g. Engineering'"
                  @keydown.enter="submit"
                  :class="error ? 'border-red-400 dark:border-red-500' : 'border-gray-200 dark:border-gray-700'"
                  class="w-full px-3.5 py-2.5 text-sm border rounded-lg bg-white dark:bg-[#0d1117] text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-shadow" />
                <p v-if="error" class="mt-1.5 text-xs text-red-500 dark:text-red-400 font-medium">{{ error }}</p>
              </div>

              <div>
                <label class="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">Description</label>
                <textarea v-model="description" rows="3" placeholder="Optional"
                  class="w-full px-3.5 py-2.5 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-[#0d1117] text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none leading-relaxed"></textarea>
              </div>

              <!-- Prefill departments (only when creating a new company) -->
              <div v-if="canPrefillDepartments" class="border-t border-gray-100 dark:border-gray-700 pt-4">
                <label class="flex items-center gap-2.5 cursor-pointer select-none py-1">
                  <input type="checkbox" v-model="prefillDepartments"
                    class="w-5 h-5 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 focus:ring-offset-0 cursor-pointer" />
                  <span class="text-sm font-medium text-gray-700 dark:text-gray-300">Prefill with departments</span>
                </label>

                <div v-if="prefillDepartments" class="grid grid-cols-2 gap-x-3 gap-y-1 mt-2 pl-6">
                  <label v-for="dept in DEFAULT_DEPARTMENT_OPTIONS" :key="dept"
                    class="flex items-center gap-2.5 cursor-pointer select-none py-1.5">
                    <input type="checkbox" :value="dept" v-model="selectedDepartments"
                      class="w-[18px] h-[18px] rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 focus:ring-offset-0 cursor-pointer" />
                    <span class="text-sm text-gray-600 dark:text-gray-400">{{ dept }}</span>
                  </label>
                </div>
              </div>
            </div>

            <div class="flex gap-3 px-6 sm:px-7 py-5 mt-2">
              <button type="button" @click="$emit('close')"
                class="flex-1 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors">
                Cancel
              </button>
              <button type="button" @click="submit" :disabled="saving"
                class="flex-1 inline-flex items-center justify-center gap-2 py-2.5 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-60 rounded-lg transition-colors">
                <svg v-if="saving" class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                </svg>
                {{ submitLabel }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { ref, computed, onMounted, nextTick } from 'vue'
import { NODE_ICONS } from 'controllers/vue_components/organizations/structure/nodeIcons.js'

const DEFAULT_DEPARTMENT_OPTIONS = ['Engineering', 'Sales', 'Marketing', 'Human Resources', 'Finance', 'Customer Support']

const props = defineProps({
  title: { type: String, required: true },
  type: { type: String, required: true },
  isEditing: { type: Boolean, default: false },
  submitLabel: { type: String, default: 'Add' },
  saving: { type: Boolean, default: false },
  initialName: { type: String, default: '' },
  initialDescription: { type: String, default: '' },
  error: { type: String, default: '' },
})

const emit = defineEmits(['close', 'submit'])

const name = ref(props.initialName)
const description = ref(props.initialDescription)
const nameInput = ref(null)

const canPrefillDepartments = computed(() => props.type === 'company' && !props.isEditing)
const prefillDepartments = ref(false)
const selectedDepartments = ref([...DEFAULT_DEPARTMENT_OPTIONS])

const infoIcon = computed(() => (props.type === 'company' ? NODE_ICONS.company : NODE_ICONS.department))

const infoHeading = computed(() => (props.type === 'company' ? 'About companies' : 'About departments'))

const infoBody = computed(() => {
  if (props.type === 'company') {
    return 'A company represents a business entity within your organization, such as a subsidiary, brand, or regional office.'
  }
  return 'A department groups the teams within a company, helping you keep the structure organized and easy to navigate.'
})

const infoPoints = computed(() => {
  if (props.type === 'company') {
    return [
      'Belongs directly to your organization',
      'Can hold multiple departments of its own',
      'Shows up in both the table and org chart views',
    ]
  }
  return [
    'Belongs to exactly one company',
    "Independent per company, they don't need to match",
    'Keeps your org structure searchable',
  ]
})

function submit() {
  if (!name.value.trim()) return

  const payload = { name: name.value.trim(), description: description.value.trim() }
  if (canPrefillDepartments.value && prefillDepartments.value) {
    payload.departments = [...selectedDepartments.value]
  }

  emit('submit', payload)
}

onMounted(() => {
  nextTick(() => nameInput.value?.focus())
})
</script>

<style scoped>
.modal-enter-active { transition: opacity 0.2s ease; }
.modal-leave-active { transition: opacity 0.15s ease; }
.modal-enter-from, .modal-leave-to { opacity: 0; }
.modal-enter-active .animate-in { transition: transform 0.2s ease; }
.modal-enter-from .animate-in { transform: scale(0.96) translateY(8px); }

.gradient-panel {
  background: linear-gradient(155deg, #1E40AF 0%, #2563EB 45%, #1D4ED8 100%);
  background-size: 200% 200%;
  animation: gradientShift 10s ease infinite;
}

@keyframes gradientShift {
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
}

.blob {
  position: absolute;
  border-radius: 9999px;
  background: rgba(255, 255, 255, 0.10);
  filter: blur(36px);
  pointer-events: none;
}

.blob-1 {
  width: 200px;
  height: 200px;
  top: -50px;
  right: -60px;
  animation: float1 9s ease-in-out infinite;
}

.blob-2 {
  width: 150px;
  height: 150px;
  bottom: -40px;
  left: -30px;
  animation: float2 11s ease-in-out infinite;
}

@keyframes float1 {
  0%, 100% { transform: translate(0, 0) scale(1); }
  50% { transform: translate(-14px, 16px) scale(1.1); }
}

@keyframes float2 {
  0%, 100% { transform: translate(0, 0) scale(1); }
  50% { transform: translate(12px, -14px) scale(1.12); }
}
</style>
